<script lang="ts">
    import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte'
    import { get } from 'svelte/store'
    import { slide } from 'svelte/transition'
    import { cubicOut } from 'svelte/easing'
    import Fa from 'svelte-fa'
    import { faPaperPlane, faCheck, faTimes, faTrashAlt, faPen, faCheckCircle, faStickyNote, faCalendarDay, faCaretDown } from '@fortawesome/free-solid-svg-icons'
    import Zinput from '../../../_common/z/zinput.svelte'
    import Zbutton from '../../../_common/z/zbutton.svelte'
    import {
        addNote,
        dayKey,
        removeFromDay,
        removeNote,
        removeOccurrence,
        updateNote,
        notesPreferredKind,
        setPreferredKind,
        type AgendaNote,
        type AgendaNoteKind,
    } from './notes-store'
    const dispatch = createEventDispatcher()

    // Campo de rodapé da coluna, no estilo de chat: recolhido é só o campo + enviar; ao tocar, as opções
    // abrem abaixo; ao enviar, cria (ou salva) e recolhe de novo.

    // dia da coluna: onde a tarefa/nota nova é criada, e o "esta" das exclusões de item que se repete
    export let date: Date
    // item tocado na lista para editar; null = criando
    export let editing: AgendaNote = null
    // há texto digitado (ou item em edição): quem controla a visibilidade do campo não pode escondê-lo
    export let dirty = false

    // Três coisas diferentes: a tarefa se faz (tem check), o lembrete vale para um dia, a nota fica
    // sempre (é o bloco de anotações — senha do alarme, texto pronto para colar).
    const kindOptions: { value: AgendaNoteKind; label: string; icon: any }[] = [
        { value: 'task', label: 'Tarefa', icon: faCheckCircle },
        { value: 'reminder', label: 'Lembrete', icon: faCalendarDay },
        { value: 'note', label: 'Nota', icon: faStickyNote },
    ]
    // seletor de tipo à esquerda do campo: botão com o ícone do tipo atual que abre as duas opções acima dele
    let kindMenu = false
    function pickKind(value: AgendaNoteKind) {
        kind = value
        if (!editing) setPreferredKind(value)
        kindMenu = false
        inputEl?.focus()
    }

    // Na tarefa e no lembrete os detalhes seguem ocultos (o campo existe e o que já estava gravado é
    // preservado). Na nota eles são o conteúdo em si — o recado que se copia, a instrução do alarme —
    // então aparecem sempre que o tipo for nota.
    $: showDetails = isNote

    let title = ''
    let details = ''
    let kind: AgendaNoteKind = get(notesPreferredKind)
    let everyDay = false
    let isPublic = false
    let expanded = false
    let confirmRemove = false

    let rootEl: HTMLElement = null
    let width = 0
    let inputEl: HTMLInputElement = null

    $: if (!editing) kind = $notesPreferredKind

    // "Em uso" é o que impede o campo de sumir quando o mouse sai da coluna (semanal): texto no título,
    // texto no conteúdo (na nota ele pode ser digitado antes do título) ou o cursor dentro do box.
    let hasFocus = false
    $: dirty = !!title.trim() || !!details.trim() || !!editing || hasFocus

    // o foco pode só estar passando de um campo do box para outro (título → conteúdo): isso não é sair
    function onFocusOut(ev: FocusEvent) {
        hasFocus = !!rootEl && rootEl.contains(ev.relatedTarget as Node)
    }
    $: isNote = kind === 'note'
    $: noun = kind === 'note' ? 'nota' : kind === 'reminder' ? 'lembrete' : 'tarefa'
    // a nota é sempre de todos os dias; trocar o tipo no box já deixa o estado certo antes de salvar
    $: if (isNote && !everyDay) everyDay = true

    // carrega o item tocado na lista (uma vez por item, para não sobrescrever o que já foi alterado)
    let loadedId: string = null
    $: loadEditing(editing)
    async function loadEditing(note: AgendaNote) {
        if (!note) {
            if (loadedId) reset()
            return
        }
        if (note.agendaNoteId === loadedId) return
        loadedId = note.agendaNoteId
        title = note.title
        details = note.details || ''
        kind = note.kind || 'task'
        everyDay = note.everyDay
        isPublic = note.isPublic
        confirmRemove = false
        expanded = true
        await tick()
        inputEl?.focus()
    }

    function reset() {
        loadedId = null
        title = ''
        details = ''
        kind = get(notesPreferredKind)
        everyDay = false
        isPublic = false
        confirmRemove = false
        expanded = false
    }

    function finish(agendaNoteId: string = null) {
        const wasEditing = !!editing
        reset()
        inputEl?.blur()
        if (wasEditing) dispatch('cancel-edit')
        if (agendaNoteId) dispatch('saved', agendaNoteId)
    }

    // criar espera a API (o id vem de lá); se falhar, o texto continua no campo para tentar de novo.
    // Editar é otimista (ver notes-store.ts): recolhe na hora.
    let saving = false
    async function send() {
        const text = title.trim()
        if (!text || saving) {
            inputEl?.focus()
            return
        }
        const input = { kind, title: text, details: details.trim(), everyDay, isPublic }
        if (editing) {
            const agendaNoteId = editing.agendaNoteId
            updateNote(agendaNoteId, input)
            finish(agendaNoteId)
            return
        }
        saving = true
        try {
            const agendaNoteId = await addNote(input, date)
            if (agendaNoteId) finish(agendaNoteId)
        } finally {
            saving = false
        }
    }

    function cancelEdit() {
        finish()
    }

    function onKeydown(ev: KeyboardEvent) {
        if (ev.key === 'Enter') {
            ev.preventDefault()
            send()
        } else if (ev.key === 'Escape') {
            if (editing) cancelEdit()
            else if (!title.trim()) finish()
        }
    }

    function onRemove() {
        if (!editing) return
        // As três opções (só hoje / daqui pra frente / todas) existem porque uma TAREFA que se repete
        // tem uma vida por dia: é concluída dia a dia, e um dia pode não valer. Lembrete e nota não
        // têm conclusão — não há o que preservar de um dia específico, então excluir é excluir de
        // todos os dias, e perguntar qual deles seria uma escolha sem consequência.
        // vale o que está gravado, não o seletor de tipo do box (que a pessoa pode ter mexido sem salvar)
        const gravado = editing.kind || 'task'
        // "este lembrete", "esta tarefa", "esta nota"
        const item = gravado === 'note' ? 'esta nota' : gravado === 'reminder' ? 'este lembrete' : 'esta tarefa'
        if (editing.everyDay && gravado === 'task') {
            confirmRemove = true
            return
        }
        const pergunta = editing.everyDay ? `Excluir ${item} de todos os dias, inclusive dos anteriores?` : `Excluir ${item}?`
        if (!confirm(pergunta)) return
        removeNote(editing.agendaNoteId)
        finish()
    }
    function onRemoveOnlyThis() {
        removeOccurrence(editing.agendaNoteId, dayKey(date))
        finish()
    }
    function onRemoveThisAndNext() {
        removeFromDay(editing.agendaNoteId, dayKey(date))
        finish()
    }
    // some de todos os dias, inclusive dos que já passaram: é a única que apaga o registro
    function onRemoveAll() {
        if (!confirm(`Excluir esta tarefa de todos os dias, inclusive dos anteriores?`)) return
        removeNote(editing.agendaNoteId)
        finish()
    }

    // Com o campo em foco, tocar em qualquer outra parte do box (tipo, toggles, espaços vazios) não tira o foco
    // dele: no celular, perder o foco fecha o teclado, e a agenda desceria e subiria de novo a cada toque.
    // Impedir o mousedown mantém o foco; o clique continua acontecendo, então toggles e botões funcionam.
    // Campos de digitar (ex.: detalhes, hoje oculto) ficam de fora, para poderem receber o foco.
    function keepInputFocus(ev: MouseEvent) {
        if (document.activeElement !== inputEl) return
        const target = ev.target as HTMLElement
        if (target === inputEl || target.closest?.('textarea, input:not([type="checkbox"]):not([type="radio"])')) return
        ev.preventDefault()
    }

    // tocar fora com o campo vazio recolhe as opções (com texto ou em edição, fica como está)
    function onPointerDownOutside(ev: PointerEvent) {
        if (kindMenu && rootEl && !rootEl.contains(ev.target as Node)) kindMenu = false
        if (!expanded || dirty) return
        if (rootEl && !rootEl.contains(ev.target as Node)) expanded = false
    }
    onMount(() => window.addEventListener('pointerdown', onPointerDownOutside, true))
    onDestroy(() => window.removeEventListener('pointerdown', onPointerDownOutside, true))
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
    class="composer"
    class:expanded
    class:narrow={width > 0 && width < 200}
    bind:clientWidth={width}
    bind:this={rootEl}
    on:mousedown={keepInputFocus}
    on:focusin={() => (hasFocus = true)}
    on:focusout={onFocusOut}
>
    {#if editing}
        <div class="composer-editing">
            <Fa icon={faPen} />
            <span class="-txt">Editando: {editing.title}</span>
            <button type="button" class="composer-editing_close" title="Cancelar edição" on:click={cancelEdit}><Fa icon={faTimes} /></button>
        </div>
    {/if}
    <div class="composer-row">
        <div class="composer-kind">
            <button
                type="button"
                class="composer-kind_button"
                class:-note={isNote}
                title={isNote ? 'Nota' : 'Tarefa'}
                on:mousedown|preventDefault
                on:click={() => {
                    kindMenu = !kindMenu
                    expanded = true
                }}
            >
                <Fa icon={isNote ? faStickyNote : faCheckCircle} />
                <span class="-caret"><Fa icon={faCaretDown} /></span>
            </button>
            {#if kindMenu}
                <div class="composer-kind_menu" transition:slide={{ duration: 120 }}>
                    {#each kindOptions as option (option.value)}
                        <button
                            type="button"
                            class="composer-kind_option -{option.value}"
                            class:active={kind === option.value}
                            on:mousedown|preventDefault
                            on:click={() => pickKind(option.value)}
                        >
                            <span class="-icon"><Fa icon={option.icon} /></span>
                            {option.label}
                        </button>
                    {/each}
                </div>
            {/if}
        </div>
        <input
            class="composer-input"
            type="text"
            placeholder={kind === 'note' ? 'Título da nota' : kind === 'reminder' ? 'Adicionar lembrete' : 'Adicionar tarefa'}
            bind:this={inputEl}
            bind:value={title}
            on:focus={() => (expanded = true)}
            on:keydown={onKeydown}
        />
        <button
            type="button"
            class="composer-send"
            class:ready={!!title.trim() && !saving}
            title={editing ? 'Salvar' : 'Enviar'}
            on:mousedown|preventDefault
            on:click={send}
        >
            <Fa icon={editing ? faCheck : faPaperPlane} />
        </button>
    </div>
    {#if expanded}
        <div class="composer-options" transition:slide={{ duration: 180, easing: cubicOut }}>
            {#if showDetails}
                <Zinput label="Conteúdo (opcional)" size={12} sizeM={12} type="textarea" rows={3} bind:value={details} />
            {/if}
            <div class="composer-switches">
                <!-- switch próprio (botão) e não o ZinputSwitch: o rótulo de um checkbox recebe o foco no clique,
                     o que fecharia o teclado do celular; um botão com o mousedown impedido não recebe (ver keepInputFocus) -->
                {#if !isNote}
                    <!-- a nota não tem dia: ela vale sempre, então o toggle não teria o que oferecer -->
                    <button type="button" class="composer-switch" role="switch" aria-checked={everyDay} on:click={() => (everyDay = !everyDay)}>
                        <span class="-track" class:on={everyDay}><span class="-knob"></span></span>
                        <span class="-label">Todos os dias</span>
                    </button>
                {/if}
                <button type="button" class="composer-switch" role="switch" aria-checked={isPublic} on:click={() => (isPublic = !isPublic)}>
                    <span class="-track" class:on={isPublic}><span class="-knob"></span></span>
                    <span class="-label">Pública</span>
                </button>
            </div>
            {#if editing}
                {#if confirmRemove}
                    <div class="composer-remove">
                        <div class="-title">Esta tarefa se repete todos os dias</div>
                        <Zbutton color="red" grow={true} label="Excluir apenas esta" on:click={onRemoveOnlyThis}></Zbutton>
                        <Zbutton color="red" grow={true} label="Excluir esta e as futuras" on:click={onRemoveThisAndNext}></Zbutton>
                        <Zbutton color="red" grow={true} label="Excluir todas" on:click={onRemoveAll}></Zbutton>
                        <Zbutton color="secondary" grow={true} label="Cancelar" on:click={() => (confirmRemove = false)}></Zbutton>
                    </div>
                {:else}
                    <button type="button" class="composer-delete" on:click={onRemove}><Fa icon={faTrashAlt} /> Excluir {noun}</button>
                {/if}
            {/if}
        </div>
    {/if}
</div>

<style>
    /* grudado na base da parte visível da coluna. No mobile, --notes-composer-bottom acompanha a barra
       inferior do app (definido em schedules-container.svelte) */
    .composer {
        position: sticky;
        bottom: var(--notes-composer-bottom, 0px);
        /* acima do cabeçalho preso da coluna (z-index 4, em schedule-column-header) e da própria
           coluna presa (5): com o teclado aberto sobra pouca altura, e o campo e o cabeçalho se
           encontram — quem tem de ficar por cima é o campo, que é onde a pessoa está digitando */
        z-index: 6;
        margin-top: auto;
        padding: 8px;
        background: #2f2f2f;
        border-top: 1px solid #ffffff12;
        box-shadow: 0px -6px 14px -8px #00000080;
        transition: bottom 0.1s linear;
    }

    .composer-editing {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #2ecc71;
        padding: 0px 2px 6px 6px;
        min-width: 0;
    }
    .composer-editing .-txt {
        flex: 1 1 10px;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .composer-editing_close {
        flex: 0 0 24px;
        height: 24px;
        border: 0px;
        border-radius: 50%;
        background: #ffffff14;
        color: #ccc;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        cursor: pointer;
    }

    .composer-row {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    /* ---- seletor de tipo (tarefa / nota) ---- */
    .composer-kind {
        position: relative;
        flex: 0 0 auto;
    }
    .composer-kind_button {
        height: 40px;
        padding: 0px 8px 0px 11px;
        border-radius: 999px;
        border: 1px solid #ffffff14;
        background: #1f1f1f;
        color: #2ecc71;
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 17px;
        cursor: pointer;
    }
    .composer-kind_button.-note {
        color: #e0b040;
    }
    .composer-kind_button .-caret {
        font-size: 10px;
        color: #8a8a8a;
    }
    /* abre para cima: o campo fica na base da coluna */
    .composer-kind_menu {
        position: absolute;
        left: 0px;
        bottom: calc(100% + 6px);
        z-index: 3;
        min-width: 140px;
        padding: 4px;
        border-radius: 10px;
        background: #262626;
        border: 1px solid #ffffff1a;
        box-shadow: 0px 8px 24px #00000080;
    }
    .composer-kind_option {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 9px 10px;
        border: 0px;
        border-radius: 7px;
        background: none;
        color: #eee;
        font-size: 14px;
        text-align: left;
        cursor: pointer;
    }
    .composer-kind_option:hover,
    .composer-kind_option.active {
        background: #ffffff12;
    }
    .composer-kind_option .-icon {
        width: 18px;
        display: flex;
        justify-content: center;
        font-size: 16px;
    }
    .composer-kind_option.-task .-icon {
        color: #2ecc71;
    }
    .composer-kind_option.-note .-icon {
        color: #e0b040;
    }
    .composer-input {
        flex: 1 1 10px;
        min-width: 0;
        height: 40px;
        border-radius: 999px;
        border: 1px solid #ffffff14;
        background: #1f1f1f;
        color: #eee;
        padding: 0px 14px;
        font-size: 14px;
        outline: none;
    }
    .composer-input:focus {
        border-color: #2ecc7188;
    }
    .composer-input::placeholder {
        color: #8a8a8a;
    }
    .composer-send {
        flex: 0 0 40px;
        height: 40px;
        border-radius: 50%;
        border: 0px;
        background: #157c53;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
        cursor: pointer;
        opacity: 0.55;
        transition:
            opacity 0.15s ease,
            transform 0.15s ease;
    }
    .composer-send.ready {
        opacity: 1;
    }
    .composer-send.ready:active {
        transform: scale(0.92);
    }

    .composer-options {
        padding: 8px 4px 0px 4px;
    }
    /* lado a lado; numa coluna estreita a segunda desce para a linha de baixo */
    .composer-switches {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        column-gap: 16px;
        row-gap: 4px;
    }
    /* coluna estreita (semanal no desktop): botões menores para sobrar espaço para o texto */
    .composer.narrow {
        padding: 6px;
    }
    .composer.narrow .composer-row {
        gap: 4px;
    }
    .composer.narrow .composer-kind_button,
    .composer.narrow .composer-send {
        height: 34px;
        font-size: 14px;
    }
    .composer.narrow .composer-kind_button {
        padding: 0px 9px;
    }
    .composer.narrow .composer-kind_button .-caret {
        display: none;
    }
    .composer.narrow .composer-send {
        flex-basis: 34px;
    }
    .composer.narrow .composer-input {
        height: 34px;
        padding: 0px 10px;
        font-size: 13px;
    }

    /* anula a grade do Zinput (no mobile, .z-col-m_w-12 força min-width: 98% e empilha os dois) */
    .composer-switch {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        max-width: 100%;
        padding: 6px 0px;
        border: 0px;
        background: none;
        color: #ddd;
        font-size: 13px;
        cursor: pointer;
    }
    .composer-switch .-label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        /* Nunito: o rótulo fica 1px acima do centro do trilho (medido no navegador) */
        position: relative;
        top: 1px;
    }
    .composer-switch .-track {
        position: relative;
        flex: 0 0 34px;
        height: 20px;
        border-radius: 999px;
        background: #555;
        transition: background-color 0.15s ease;
    }
    .composer-switch .-track.on {
        background: #157c53;
    }
    .composer-switch .-knob {
        position: absolute;
        top: 3px;
        left: 3px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #fff;
        transition: transform 0.15s ease;
    }
    .composer-switch .-track.on .-knob {
        transform: translateX(14px);
    }
    .composer-delete {
        margin-top: 12px;
        border: 0px;
        background: none;
        color: #e06663;
        font-size: 13px;
        font-weight: 600;
        padding: 4px 2px;
        cursor: pointer;
    }
    .composer-remove {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 12px;
    }
    .composer-remove .-title {
        font-size: 13px;
        font-weight: 600;
        opacity: 0.85;
    }

    /* ---- tema claro ---- */
    :global(.theme_light) .composer {
        background: #f6f7f7;
        border-top-color: #e8eaed;
        box-shadow: 0px -6px 14px -10px #00000030;
    }
    :global(.theme_light) .composer-input {
        background: #fff;
        border-color: #dadce0;
        color: #202124;
    }
    :global(.theme_light) .composer-input:focus {
        border-color: #1b7a52;
    }
    :global(.theme_light) .composer-editing {
        color: #1b7a52;
    }
    :global(.theme_light) .composer-editing_close {
        background: #0000000f;
        color: #5f6368;
    }
    :global(.theme_light) .composer-send {
        background: #1b7a52;
    }
    :global(.theme_light) .composer-switch {
        color: #3c4043;
    }
    :global(.theme_light) .composer-switch .-track {
        background: #c4c7c5;
    }
    :global(.theme_light) .composer-switch .-track.on {
        background: #1b7a52;
    }
    :global(.theme_light) .composer-kind_button {
        background: #fff;
        border-color: #dadce0;
        color: #1b7a52;
    }
    :global(.theme_light) .composer-kind_button.-note {
        color: #c48a0c;
    }
    :global(.theme_light) .composer-kind_menu {
        background: #fff;
        border-color: #e8eaed;
        box-shadow: 0px 8px 24px #0000002e;
    }
    :global(.theme_light) .composer-kind_option {
        color: #202124;
    }
    :global(.theme_light) .composer-kind_option:hover,
    :global(.theme_light) .composer-kind_option.active {
        background: #0000000a;
    }
    :global(.theme_light) .composer-kind_option.-task .-icon {
        color: #1b7a52;
    }
    :global(.theme_light) .composer-kind_option.-note .-icon {
        color: #c48a0c;
    }

    @media print {
        .composer {
            display: none;
        }
    }
</style>
